import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, Database, TrendingUp, Layers, Brain, MessageSquare, Monitor, Server, DollarSign, Users, Briefcase, GraduationCap, Clock, CheckCircle2, Zap, Mail, Calendar, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_ICONS = {
  "Data Engineering": Database,
  "Supervised Algorithm": TrendingUp,
  "Unsupervised Algorithm": Layers,
  "Deep Learning": Brain,
  "Natural Language Processing": MessageSquare,
  "AI-Native App Dev": Zap,
};

const courses = [
  "Data Engineering",
  "Supervised Algorithm",
  "Unsupervised Algorithm",
  "Deep Learning",
  "Natural Language Processing",
  "AI-Native App Dev",
];

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name });
      setSubmitted(true);
    } catch {}
    setSubmitting(false);
  };

  return (
    <section data-testid="newsletter-section" className="py-24 px-6 bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto text-center">
        <Mail className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" strokeWidth={1.5} />
        <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Stay Informed</p>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Get Free AI Career Insights
        </h2>
        <p className="text-base text-zinc-300 mb-8">Join our newsletter for industry trends, job market updates, and exclusive enrollment offers. No spam, ever.</p>
        {submitted ? (
          <div className="flex items-center justify-center gap-2 text-green-400 text-lg">
            <CheckCircle2 className="w-6 h-6" strokeWidth={1.5} /> Subscribed! Check your inbox.
          </div>
        ) : (
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <input
              data-testid="newsletter-name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500 flex-1"
              placeholder="Your name"
            />
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="px-4 py-3 bg-[#111111] border border-[#262626] focus:border-[#0077B6] outline-none text-base text-white placeholder-zinc-500 flex-1"
              placeholder="your@email.com"
            />
            <button type="submit" disabled={submitting} data-testid="newsletter-submit" className="brand-btn text-base px-6 py-3 whitespace-nowrap disabled:opacity-50">
              {submitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [partners, setPartners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [team, setTeam] = useState([]);
  const [advisory, setAdvisory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/employer-partners`).then(r => { if (Array.isArray(r.data)) setPartners(r.data); }).catch(() => {});
    axios.get(`${API}/admin/testimonials`).then(r => { if (Array.isArray(r.data)) setTestimonials(r.data); }).catch(() => {});
    axios.get(`${API}/admin/team`).then(r => {
      const data = Array.isArray(r.data) ? r.data : [];
      setTeam(data.filter(t => t.board_type === 'team'));
      setAdvisory(data.filter(t => t.board_type === 'advisory'));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title=""
        description="Institute of Analytics USA - Learn AI, ML, DS - Get Hired. PG Diploma in AI, ML & Data Science Engineering. 6 courses, 120 hours, 200+ graduates, 100% employed, $80K+ starting salary."
        path="/"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden py-32 px-6">
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-4xl">
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-6 animate-fade-in-up" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Institute of Analytics USA&trade; &mdash; PG Diploma in AI, ML & Data Science Engineering
            </p>
            <h1 data-testid="hero-heading" className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-8 animate-fade-in-up delay-100 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Learn AI.<br />
              <span className="text-[#0077B6]">Get Hired.</span>
            </h1>
            <p className="text-lg md:text-xl text-white max-w-2xl mb-10 animate-fade-in-up delay-200 leading-relaxed">
              6 intensive courses. 120 hours of hands-on training. <strong className="text-[#F59E0B]">200 graduates trained, 100% employed.</strong> From data engineering to deep learning to production deployment. Graduate job-ready with $80K+ starting salary potential.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
              <Link to="/register" data-testid="hero-enroll-btn" className="brand-btn text-base px-8 py-4 flex items-center gap-2">
                Start Your Journey <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
              </Link>
              <Link to="/courses" data-testid="hero-explore-btn" className="border-2 border-white text-white px-8 py-4 font-medium hover:-translate-y-1 hover:shadow-[4px_4px_0px_#CC0000] transition-all duration-150 flex items-center gap-2">
                Explore Curriculum <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          {/* Stats Row */}
          <div data-testid="stats-row" className="grid grid-cols-2 md:grid-cols-5 gap-0 mt-24 border border-[#262626]">
            {[
              { label: "Graduates Trained", value: "200+", icon: GraduationCap },
              { label: "Employment Rate", value: "100%", icon: CheckCircle2 },
              { label: "Courses", value: "6", icon: BookOpen },
              { label: "Hours of Training", value: "120", icon: Clock },
              { label: "Starting Salary", value: "$80K+", icon: Briefcase },
            ].map((stat, i) => (
              <div key={i} className={`p-8 bg-[#111111] ${i < 4 ? 'border-r border-[#262626]' : ''} animate-fade-in-up`} style={{ animationDelay: `${0.4 + i * 0.1}s` }}>
                <stat.icon className="w-10 h-10 text-[#F59E0B] mb-3" strokeWidth={1.5} />
                <p className="text-3xl md:text-4xl font-black tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{stat.value}</p>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-300 mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSE MARQUEE */}
      <section className="py-4 bg-[#0077B6] overflow-hidden">
        <div className="flex whitespace-nowrap">
          <div className="marquee-track flex items-center gap-12 pr-12">
            {[...courses, ...courses].map((c, i) => {
              const Icon = COURSE_ICONS[c];
              return (
                <span key={i} className="flex items-center gap-2 text-white/90 text-sm font-medium">
                  {Icon && <Icon className="w-4 h-4" strokeWidth={1.5} />}
                  {c}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7 COURSES GRID */}
      <section data-testid="courses-section" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Curriculum</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            6 Courses. Complete Mastery.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#262626]">
            {courses.map((name, i) => {
              const Icon = COURSE_ICONS[name];
              const slugs = ["data-engineering","supervised-algorithm","unsupervised-algorithm","deep-learning","natural-language-processing","ai-native-app-dev"];
              return (
                <Link
                  key={i}
                  to={`/courses/${slugs[i]}`}
                  data-testid={`course-card-${i}`}
                  className="p-8 border-b border-r border-[#262626] bg-[#111111] hover:bg-[#1a1a1a] transition-colors duration-150 group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xl font-mono font-bold text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>0{i + 1}</span>
                    {Icon && <Icon className="w-10 h-10 text-[#0077B6]" strokeWidth={1.5} />}
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight mb-2 text-white group-hover:text-[#0077B6] transition-colors" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {name}
                  </h3>
                  <p className="text-base text-zinc-300">20 hours / 10 modules / $595</p>
                  <ArrowRight className="w-4 h-4 mt-4 text-zinc-400 group-hover:text-[#F59E0B] group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </Link>
              );
            })}
            {/* 8th cell: CTA */}
            <div className="p-8 border-b border-r border-[#262626] bg-[#0077B6] text-white flex flex-col justify-center">
              <Zap className="w-6 h-6 mb-4" strokeWidth={1.5} />
              <p className="text-lg font-semibold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Ready to begin?</p>
              <p className="text-sm text-white/70 mb-4">Enroll in all 6 for $2,970</p>
              <Link to="/register" className="border-2 border-white text-white px-4 py-2 text-sm font-medium inline-block w-fit hover:bg-white hover:text-[#0077B6] transition-colors">
                Enroll Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ROI SECTION */}
      <section data-testid="roi-section" className="py-32 px-6 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Return on Investment</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            The Numbers Don't Lie.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#262626]">
            <div className="p-12 border-r border-[#262626] bg-[#0A0A0A]">
              <p className="text-5xl font-black tracking-tight text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>$2,970</p>
              <p className="text-base text-zinc-200 mt-2">Total program investment</p>
              <p className="text-xs text-zinc-400 mt-1">6 courses × $595 each &middot; or save $600 with the $2,970 PG Diploma bundle</p>
            </div>
            <div className="p-12 border-r border-[#262626] bg-[#0A0A0A]">
              <p className="text-5xl font-black tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>$80K+</p>
              <p className="text-base text-zinc-200 mt-2">Starting annual salary</p>
              <p className="text-xs text-zinc-400 mt-1">For AI/ML/DS graduates</p>
            </div>
            <div className="p-12 bg-[#0A0A0A]">
              <p className="text-7xl font-black tracking-tight text-[#F59E0B]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>27x</p>
              <p className="text-base text-zinc-200 mt-2">Return on investment</p>
              <p className="text-xs text-zinc-400 mt-1">Program pays for itself in month 1</p>
            </div>
          </div>
          
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-xl font-semibold tracking-tight mb-6 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>What You Get</h3>
              <ul className="space-y-4">
                {[
                  "120 hours of expert-led lectures & practicals",
                  "Hands-on projects with real-world datasets",
                  "AI-powered job matching upon graduation",
                  "Direct employer connections through our network",
                  "Portfolio-ready capstone projects",
                  "Lifetime access to course materials"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-white/90">
                    <CheckCircle2 className="w-8 h-8 text-[#0077B6] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold tracking-tight mb-6 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Pricing That Works</h3>
              <p className="text-base text-white/80 leading-relaxed mb-6">
                At $24.50 per lecture hour, our program is priced for accessibility without compromising quality. 
                Each course is self-contained, so you can start with one and add more at your own pace.
              </p>
              <Link to="/pricing" className="brand-btn text-sm px-6 py-3 inline-flex items-center gap-2">
                View Payment Plans <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* JOB BOARD TEASER */}
      <section data-testid="jobs-teaser" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Career Support</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                AI-Powered Job Matching
              </h2>
              <p className="text-base text-white/80 leading-relaxed mb-8">
                Our platform uses AI to scan the job market for AI/ML/DS positions and matches them with your skills. Employers can directly browse graduate profiles and reach out.
              </p>
              <div className="flex gap-4">
                <Link to="/jobs" className="brand-btn text-sm px-6 py-3 flex items-center gap-2">
                  Browse Jobs <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <Link to="/register" className="brand-btn-red text-sm px-6 py-3">
                  For Employers
                </Link>
              </div>
            </div>
            <div className="border border-[#262626] p-1">
              <div className="bg-[#111111] p-8 space-y-4">
                {["ML Engineer at Google", "Data Scientist at Meta", "AI Researcher at OpenAI"].map((job, i) => (
                  <div key={i} className="bg-[#0A0A0A] border border-[#262626] p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-white">{job.split(' at ')[0]}</p>
                      <p className="text-xs text-zinc-300">{job.split(' at ')[1]}</p>
                    </div>
                    <span className="text-xs font-mono text-[#0077B6] bg-[#0077B6]/10 px-2 py-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{92 - i * 3}% match</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRI-STATE EMPLOYER PARTNERS */}
      <section data-testid="employer-partners-section" className="py-32 px-6 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Where Our Graduates Work</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Tri-State Employer Network
          </h2>
          <p className="text-base text-zinc-300 mb-16 max-w-2xl">Our graduates are hired by leading companies across Pennsylvania, New York, and New Jersey. These organizations actively recruit from our program.</p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 border border-[#262626]">
            {partners.map((company, i) => (
              <div key={i} data-testid={`partner-${i}`} className="p-6 border-r border-b border-[#262626] bg-[#0A0A0A] hover:bg-[#1a1a1a] transition-colors group">
                <div className="w-10 h-10 bg-[#0077B6]/10 border border-[#0077B6]/20 flex items-center justify-center mb-3">
                  <span className="text-lg font-black text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{company.name.charAt(0)}</span>
                </div>
                <p className="font-semibold text-sm text-white group-hover:text-[#0077B6] transition-colors" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{company.name}</p>
                <p className="text-xs text-zinc-400 mt-1">{company.location}</p>
                <span className="inline-block mt-2 text-xs font-mono px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{company.sector}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 border border-[#262626] bg-[#0A0A0A]">
            <p className="text-base text-zinc-300">
              <span className="text-white font-semibold">Hiring AI talent?</span> Become a Preferred Employer Partner and get direct access to our graduates.
            </p>
            <Link to="/partners" className="brand-btn text-sm px-6 py-3 flex items-center gap-2 whitespace-nowrap">
              Apply as Partner <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-choose-section" className="py-32 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Why Choose Us</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Built for Outcomes, Not Just Certificates.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#262626]">
            {[
              { icon: GraduationCap, title: "Industry-Aligned Curriculum", desc: "6 courses designed with real industry requirements. From SQL to Transformers to production deployment." },
              { icon: Briefcase, title: "Job Placement Support", desc: "AI-powered job matching, employer network, and personalized recommendation letters from program director." },
              { icon: Users, title: "Small Cohort Experience", desc: "Only 20 students per batch from USA/Europe + 10 from India. Direct access to instructors and peer collaboration." },
              { icon: Zap, title: "Immediate ROI", desc: "$2,970 investment leads to $80K+ salary. Program pays for itself in month one of employment." },
            ].map((item, i) => (
              <div key={i} className="p-8 border-r border-b border-[#262626] bg-[#111111]">
                <item.icon className="w-14 h-14 text-[#F59E0B] mb-4" strokeWidth={1.5} />
                <h3 className="font-semibold tracking-tight mb-2 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{item.title}</h3>
                <p className="text-base text-zinc-300 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Success Stories</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            From Enrollment to Employment.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#262626]">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="p-8 border-r border-[#262626] bg-[#111111] flex flex-col justify-between">
                <div>
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="w-8 h-8 bg-[#CC0000]" />
                    ))}
                  </div>
                  <p className="text-base text-white/80 leading-relaxed italic mb-6">"{t.quote}"</p>
                </div>
                <div className="pt-4 border-t border-[#262626]">
                  <p className="font-medium text-sm text-white">{t.name}</p>
                  <p className="text-xs text-zinc-300">{t.role} at {t.company}</p>
                  <p className="text-xs font-mono text-[#0077B6] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Starting: {t.salary}/yr</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP & ADVISORY BOARD */}
      <section data-testid="leadership-section" className="py-32 px-6 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Leadership</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Faculty & Advisory Board
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#262626] mb-12">
            {team.map((person, i) => (
              <div key={i} className="p-6 border-r border-b border-[#262626] bg-[#0A0A0A]">
                <div className="w-12 h-12 bg-[#0077B6]/10 border border-[#0077B6]/20 flex items-center justify-center mb-4">
                  <span className="text-sm font-bold text-[#0077B6]">{person.name.split(' ').map(w => w[0]).join('').slice(0,2)}</span>
                </div>
                <p className="font-semibold text-sm text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{person.name}</p>
                <p className="text-xs text-[#F59E0B] font-bold mt-1">{person.role}</p>
                <p className="text-xs text-zinc-400 mt-1">{person.focus}</p>
              </div>
            ))}
          </div>
          <h3 className="text-lg font-semibold text-[#0077B6] mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Advisory Board</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#262626]">
            {advisory.map((person, i) => (
              <div key={i} className="p-6 border-r border-b border-[#262626] bg-[#0A0A0A]">
                <p className="font-semibold text-sm text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{person.name}</p>
                <p className="text-xs text-[#0077B6] mt-1">{person.role}</p>
                <p className="text-xs text-zinc-400">{person.org}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section data-testid="cta-section" className="py-24 px-6 bg-[#0077B6]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-4 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Ready to Transform Your Career?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Join 200+ graduates already working in AI. Start with one course at $595 or enroll in all 6. Your $80K+ career starts here.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/register" className="bg-white text-[#0077B6] font-medium text-base px-8 py-4 flex items-center gap-2 hover:-translate-y-1 hover:shadow-[4px_4px_0px_#CC0000] transition-all duration-150">
              Enroll Now <ArrowRight className="w-5 h-5" strokeWidth={1.5} />
            </Link>
            <Link to="/courses" className="border-2 border-white text-white px-8 py-4 font-medium hover:bg-white hover:text-[#0077B6] transition-colors flex items-center gap-2">
              View Curriculum
            </Link>
          </div>
        </div>
      </section>

      {/* NEWSLETTER SIGNUP */}
      <NewsletterSection />

      {/* EXPLORE MORE */}
      <section className="py-20 px-6 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#262626]">
            <Link to="/blog" className="p-8 border-r border-[#262626] bg-[#0A0A0A] hover:bg-[#1a1a1a] transition-colors group">
              <BookOpen className="w-10 h-10 text-[#0077B6] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-white group-hover:text-[#0077B6] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>AI Career Insights</h3>
              <p className="text-base text-zinc-300">Read our blog for industry trends, career advice, and student success stories.</p>
            </Link>
            <Link to="/events" className="p-8 border-r border-[#262626] bg-[#0A0A0A] hover:bg-[#1a1a1a] transition-colors group">
              <Calendar className="w-10 h-10 text-[#F59E0B] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-white group-hover:text-[#0077B6] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Events & Meetups</h3>
              <p className="text-base text-zinc-300">Join free info sessions, workshops, and community meetups. Meet us in person.</p>
            </Link>
            <Link to="/partners" className="p-8 bg-[#0A0A0A] hover:bg-[#1a1a1a] transition-colors group">
              <Briefcase className="w-10 h-10 text-[#0077B6] mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-white group-hover:text-[#0077B6] mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Employer Partners</h3>
              <p className="text-base text-zinc-300">Hire our graduates. Apply for Preferred Employer status and access top AI talent.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="footer" className="border-t border-[#262626] py-16 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 overflow-hidden flex items-center">
                <img src="/ioa_logo_dark.svg" alt="Institute of Analytics USA" className="h-16 w-auto object-cover" style={{ objectPosition: 'center' }} />
              </div>
            </div>
            <p className="text-base text-zinc-300 max-w-sm">PG Diploma in AI, ML & Data Science Engineering by Institute of Analytics USA&trade;. 6 courses designed to launch your career in artificial intelligence.</p>
          </div>
          <div className="flex gap-16">
            <div>
              <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Program</p>
              <div className="space-y-2">
                <Link to="/courses" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Courses</Link>
                <Link to="/pricing" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Pricing</Link>
                <Link to="/jobs" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Job Board</Link>
                <Link to="/partners" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Employer Partners</Link>
                <Link to="/blog" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Blog</Link>
                <Link to="/events" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Events</Link>
              </div>
            </div>
            <div>
              <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Account</p>
              <div className="space-y-2">
                <Link to="/login" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Sign In</Link>
                <Link to="/register" className="block text-sm text-zinc-200 hover:text-[#0077B6]">Enroll</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
